/**
 * App-wide (NOT league-scoped) runtime configuration, stored in the single
 * Firestore doc `config/app`. World-readable so the client can read it even
 * before sign-in; written ONLY by the Admin SDK (the password-gated
 * `setAppConfig` callable), never by clients — the same trust model as every
 * other engine-owned doc.
 *
 * Today it carries the force-update gate. The nested shape leaves room for other
 * app-global flags later without changing the doc's contract.
 */
/**
 * Compare two dotted-numeric version strings ("1.2", "1.2.0", "1.10.3").
 * Returns -1 when a < b, 1 when a > b, 0 when equal. Missing trailing parts
 * count as 0 (so "1.2" === "1.2.0"); non-numeric segments (e.g. a "-beta" tag)
 * are treated as 0. Note this is numeric per-segment, so "1.10" > "1.9".
 */
export function compareVersions(a, b) {
    const parse = (v) => String(v ?? '')
        .split('.')
        .map((part) => parseInt(part, 10) || 0);
    const pa = parse(a);
    const pb = parse(b);
    const len = Math.max(pa.length, pb.length);
    for (let i = 0; i < len; i++) {
        const da = pa[i] ?? 0;
        const db = pb[i] ?? 0;
        if (da !== db)
            return da > db ? 1 : -1;
    }
    return 0;
}
/**
 * The single pure rule the client gate (and any server check) share: is this
 * install below the configured floor, and therefore blocked?
 *
 * Fails OPEN by design — an absent/disabled config, or one with no threshold
 * set, never blocks — so a config slip or a failed read can never lock players
 * out of the app. A player is blocked only when the gate is explicitly enabled
 * AND their install is below a threshold that was explicitly set.
 */
export function isUpdateRequired(cfg, installed) {
    if (!cfg || cfg.enabled !== true)
        return false;
    const minVersion = typeof cfg.minVersion === 'string' ? cfg.minVersion.trim() : '';
    if (minVersion && compareVersions(installed.version, minVersion) < 0)
        return true;
    const hasMinBuild = typeof cfg.minBuild === 'number' && Number.isFinite(cfg.minBuild);
    if (hasMinBuild && Number.isFinite(installed.build) && installed.build < cfg.minBuild) {
        return true;
    }
    return false;
}
/**
 * Sanitise raw admin input into a clean {@link ForceUpdateConfig} for storage:
 * coerce types, trim strings, drop blanks. Only keys that carry a real value
 * (besides `enabled`) are kept, so clearing a field in the admin form removes it
 * from the stored doc rather than persisting an empty string.
 */
export function normalizeForceUpdateConfig(input) {
    const raw = (input && typeof input === 'object' ? input : {});
    const str = (v) => (typeof v === 'string' ? v.trim() : '');
    const out = { enabled: raw.enabled === true };
    const minVersion = str(raw.minVersion);
    if (minVersion)
        out.minVersion = minVersion;
    const minBuild = Number(raw.minBuild);
    if (Number.isFinite(minBuild) && minBuild > 0)
        out.minBuild = Math.floor(minBuild);
    const latestVersion = str(raw.latestVersion);
    if (latestVersion)
        out.latestVersion = latestVersion;
    const title = str(raw.title);
    if (title)
        out.title = title;
    const message = str(raw.message);
    if (message)
        out.message = message;
    const iosUrl = str(raw.iosUrl);
    if (iosUrl)
        out.iosUrl = iosUrl;
    return out;
}
//# sourceMappingURL=appConfig.js.map